import { TEulaAcceptance, TRepresentativeFormData } from '@/modules/onboarding/organization/types';
import { TCustomAxiosError } from '@/services/api';
import { acceptedDocumentsGet, acceptEulaDocumentPost } from '@/services/api/requests/legal';
import {
  inviteUsersToOrganizationPost,
  myOrganizationGet,
  updateOrganizationByIdPut,
  uploadOrganizationLogoPost,
} from '@/services/api/requests/organization';
import { useAppStore } from '@/store/useAppStore';
import { TOrganization } from '@/types/Organization.types';
import { useMutation } from '@tanstack/react-query';
import useToast from '../useToast';
import { TParsedRosterList, TPeerSupportCertificateInfoMap } from '@/store/useOrganizationOnboardingStore';

interface ISubmitOrganization {
  organizationId: string;
  formData: TRepresentativeFormData;
  eulaAcceptance: TEulaAcceptance | null;
  logoFile: File;
  roster: TParsedRosterList;
  peerSupportCertificateData: TPeerSupportCertificateInfoMap;
}

const trimInviteValue = (value: string | undefined) => value?.trim() ?? '';

const useSubmitOrganization = (onSuccessCallback?: (organization: TOrganization) => void) => {
  const toast = useToast();

  const { isEulaAccepted: isEulaAcceptedInit, legalAcceptanceStatuses, setLegalAcceptanceStatuses } = useAppStore();

  const { isPending, mutate, isError } = useMutation({
    mutationFn: async ({
      formData,
      logoFile,
      roster,
      peerSupportCertificateData,
      organizationId,
      eulaAcceptance,
    }: ISubmitOrganization) => {
      const promises = [];

      const { isEulaAccepted: eulaCheckboxBoolean, ...restFormData } = formData;

      const updateOrg = updateOrganizationByIdPut(organizationId, restFormData);
      promises.push(updateOrg);

      if (roster && roster.length > 0) {
        const usersToInvite = roster.map((person, i) => ({
          email: trimInviteValue(person['Email']),
          phone: trimInviteValue(person['Phone']),
          firstName: trimInviteValue(person['First Name']),
          lastName: trimInviteValue(person['Last Name']),
          userAffiliation: trimInviteValue(person['Affiliation']),
          isPeerSupport: person['Peer Support'],
          isPeerSupportLead: person['Peer Support Lead'],
          certificateId: peerSupportCertificateData?.get(i)?.certificateId,
          validationStartDate: peerSupportCertificateData?.get(i)?.dateOfIssue,
          validationEndDate: peerSupportCertificateData?.get(i)?.validUntil,
        }));

        const inviteUsers = inviteUsersToOrganizationPost(usersToInvite);

        promises.push(inviteUsers);
      }

      if (!isEulaAcceptedInit && eulaCheckboxBoolean && eulaAcceptance !== null) {
        const documentIds = legalAcceptanceStatuses?.map((document) => document._id) || [];
        const promises = documentIds.map((documentId) => acceptEulaDocumentPost(documentId));
        promises.push(...promises);
      }

      const uploadLogo = uploadOrganizationLogoPost(logoFile);
      promises.push(uploadLogo);

      // TODO: EULA - add accept request
      await Promise.all(promises);

      const updatedOrg = await myOrganizationGet();

      const updatedLegalStatues = await acceptedDocumentsGet();
      setLegalAcceptanceStatuses(updatedLegalStatues);

      return { organization: updatedOrg };
    },
    onSuccess: ({ organization }) => {
      if (onSuccessCallback) {
        onSuccessCallback(organization);
      }
    },
    onError: (error: TCustomAxiosError) => {
      toast(
        {
          title: 'Error during onboarding',
        },
        error,
      );
    },
  });

  return { isPending, mutate, isError };
};

export default useSubmitOrganization;
